/**
 * Box 3 "Metric เฉพาะ <channel>" and the GA4 tiles (spec §3.3, §3.5; rulings R14,
 * R15), plus the GA4 chart's points. One registry per platform, in the
 * prototype's order. Every ratio divides sums over the rows that report its
 * bracketed metric. A data tile reads "ไม่ได้รายงาน" only when every row is NULL for
 * its metric, and "—" when it has nothing to count (a conversion tile whose
 * whole period is after the cut). A ratio tile is n<500 when box 2's CTR / CPC /
 * CPM are (rules-04 D1), unless it is not reported; a count never is. Δ tone
 * comes from D1's one direction map and tone function (lookerDelta, rules-04 D3)
 * on the raw Δ. Under objective "all", CPA, CPL and ROAS never pool objectives
 * (rules-04 E4): they read the Conversion objective's rows only, in every
 * window, so each is the tile the Conversion filter shows, its n<500 judged on
 * those rows, and it says so; the counts beside them stay totals. With one
 * objective selected they read its rows, as every tile does, and name it too,
 * so their label always says which objective they read (rules-04 H1). When
 * that objective has no row in the page's scope in any currency, they read "—"
 * and say why (rules-04 H2). Pure.
 *
 * @module services/channels/platformTiles
 */
import type { DateRange } from '@/services/looker/lookerDateRange';
import { deltaOf, directionOf, toneFor, type MetricDirection } from '@/services/looker/lookerDelta';
import { isLowSample, totalsOf } from '@/services/overview/overviewMath';
import type { FactRow, ObjectiveFilter } from '@/services/overview/overviewTypes';
import { isoWeekStart, trendOf } from '@/services/overview/spendTrend';
import { ratioOverReporting, reportingRows, sumOf } from './channelMath';
import type {
  ChannelFactRow, ChannelPlatform, Ga4Point, PlatformTile, TileFormat, TileMetric, TileRows, TileState,
} from './channelTypes';

export interface TileSpec {
  metric: TileMetric;
  format: TileFormat;
  /** The conversion family: counted through today − 3, Δ over the cut windows (FR-X-01). */
  cut: boolean;
  /** What a rise means: D1's one map (rules-04 D3). Static tiles are neutral. */
  direction: MetricDirection;
  /** 'data' reads rows; a static tile reads nothing and always shows its state. */
  source: 'data' | 'notReported' | 'notShown';
}

const MONEY: ReadonlySet<TileMetric> = new Set<TileMetric>(['cpl', 'cpa', 'conversionValue']);

/** The conversion family (spec §3.3). GA4's web conversions are not in it: GA4 has no cut (R15). */
const CUT: ReadonlySet<TileMetric> = new Set<TileMetric>(['leads', 'cpl', 'conversions', 'conversionValue', 'roas', 'cpa']);

/**
 * The costs that never pool objectives (rules-04 E4): under "all" an Awareness campaign's spend would ride on a
 * Conversion campaign's leads. They always name the objective they read (rules-04 H1). CPV and View rate follow the
 * objective filter, as box 2's CPC, CPM and CTR do (owner, 2026-10-19).
 */
const CONVERSION_ONLY: ReadonlySet<TileMetric> = new Set<TileMetric>(['cpa', 'cpl', 'roas']);

const formatOf = (m: TileMetric): TileFormat => {
  if (m === 'viewRate') return 'pct';
  if (m === 'roas') return 'roas';
  if (m === 'cpv') return 'money4';
  return MONEY.has(m) ? 'money' : 'count';
};

const data = (metric: TileMetric): TileSpec =>
  ({ metric, format: formatOf(metric), cut: CUT.has(metric), direction: directionOf(metric), source: 'data' });

/** A static tile: count, never cut, neutral; it reads no data. */
const fixed = (metric: TileMetric, source: 'notReported' | 'notShown'): TileSpec =>
  ({ metric, format: 'count', cut: false, direction: 'neutral', source });

export const PLATFORM_TILES: Readonly<Record<ChannelPlatform, readonly TileSpec[]>> = {
  facebook: [data('reactions'), data('comments'), data('shares'), data('linkClicks'), data('leads'), data('cpl')],
  tiktok: [data('videoViews'), data('viewRate'), data('cpv'), data('conversions'), data('conversionValue'), data('roas')],
  google: [
    data('conversions'), data('cpa'), data('conversionValue'), data('roas'), data('engagements'),
    fixed('reach', 'notReported'),
  ],
  // Reach and frequency are never shown (FR-X-03); LINE's stored engagements are 0s, not measurements (FR-D2-13).
  line: [
    fixed('reach', 'notShown'), fixed('frequency', 'notShown'), data('videoCompletions'), data('conversions'),
    fixed('engagements', 'notReported'), data('linkClicks'),
  ],
  // GA4 stores one "All Traffic" row a day: no engaged sessions, no paid split (spec §3.5).
  analytics: [
    data('sessions'), fixed('engagedSessions', 'notReported'), data('webConversions'), fixed('paidShare', 'notReported'),
  ],
};

// ---------------------------------------------------------------- arithmetic

type Value = (r: ChannelFactRow) => number | null;

/** A data tile's arithmetic: a sum, or Σnum ÷ Σden over the rows that report `reportedBy` (R14). */
type Measure =
  | { kind: 'sum'; value: Value }
  | { kind: 'ratio'; num: Value; den: Value; reportedBy: Value };

const sum = (value: Value): Measure => ({ kind: 'sum', value });
const ratio = (num: Value, den: Value, reportedBy: Value): Measure => ({ kind: 'ratio', num, den, reportedBy });

const spend: Value = (r) => r.spend;
const conversions: Value = (r) => r.conversions;
const conversionValue: Value = (r) => r.conversionValue;
const leads: Value = (r) => r.leads;
const videoViews: Value = (r) => r.videoViews;

/** null = a metric that is only ever a static tile. */
const MEASURES: Readonly<Record<TileMetric, Measure | null>> = {
  reactions: sum((r) => r.reactions),
  comments: sum((r) => r.comments),
  shares: sum((r) => r.shares),
  linkClicks: sum((r) => r.linkClicks),
  leads: sum(leads),
  cpl: ratio(spend, leads, leads),
  videoViews: sum(videoViews),
  viewRate: ratio(videoViews, (r) => r.impressions, videoViews),
  cpv: ratio(spend, videoViews, videoViews),
  conversions: sum(conversions),
  conversionValue: sum(conversionValue),
  roas: ratio(conversionValue, spend, conversionValue),
  cpa: ratio(spend, conversions, conversions),
  engagements: sum((r) => r.engagements),
  // LINE sends completions; they are stored in video_views and never pooled with views (spec §3.3).
  videoCompletions: sum(videoViews),
  sessions: sum((r) => r.sessions),
  // GA4's web conversions are its rows' conversions (R3).
  webConversions: sum(conversions),
  reach: null,
  frequency: null,
  engagedSessions: null,
  paidShare: null,
};

/** LINE does not report link clicks separately: its tile is `clicks`, the Clicks scorecard (spec §3.3). */
const LINE_LINK_CLICKS: Measure = sum((r) => r.clicks);

const measureOf = (platform: ChannelPlatform, s: TileSpec): Measure | null => {
  if (s.source !== 'data') return null;
  return platform === 'line' && s.metric === 'linkClicks' ? LINE_LINK_CLICKS : MEASURES[s.metric];
};

const valueOf = (m: Measure, rows: readonly ChannelFactRow[]): number | null =>
  (m.kind === 'sum' ? sumOf(rows, m.value) : ratioOverReporting(rows, m.num, m.den, m.reportedBy));

/** none = nothing to count; notReported = every row NULL for the metric (a ratio: for its reportedBy metric). */
const stateOf = (m: Measure, rows: readonly ChannelFactRow[]): TileState => {
  if (rows.length === 0) return 'none';
  return reportingRows(rows, m.kind === 'sum' ? m.value : m.reportedBy).length === 0 ? 'notReported' : 'value';
};

const staticTile = (s: TileSpec): PlatformTile => ({
  metric: s.metric, format: s.format, cut: s.cut, isStatic: true,
  state: s.source === 'notShown' ? 'notShown' : 'notReported',
  value: null, previous: null, delta: null, tone: 'neutral', lowSample: false, objective: null, noObjectiveRows: false,
});

/** `low`: the period's impressions are under 500: box 2's (rules-04 D1), or a narrowed tile's Conversion rows' (rules-04 E4). */
const tileOf = (platform: ChannelPlatform, s: TileSpec, rows: TileRows, low: boolean): PlatformTile => {
  const measure = measureOf(platform, s);
  if (measure === null) return staticTile(s);
  const current = s.cut ? rows.cut : rows.current;
  const state = stateOf(measure, current);
  const value = state === 'value' ? valueOf(measure, current) : null;
  const previous = valueOf(measure, s.cut ? rows.cutPrevious : rows.previous);
  const delta = deltaOf(value, previous);
  return {
    metric: s.metric, format: s.format, cut: s.cut, isStatic: false,
    state, value, previous, delta, tone: toneFor(s.direction, delta),
    // A count never fades, and nor does a ratio the platform did not report (rules-04 D review M4).
    lowSample: measure.kind === 'ratio' && state !== 'notReported' && low,
    objective: null,
    noObjectiveRows: false,
  };
};

/** Every window's Conversion-objective rows: what the Conversion filter hands tilesOf. */
const conversionRowsOf = (rows: TileRows): TileRows => {
  const own = (list: readonly ChannelFactRow[]): ChannelFactRow[] => list.filter((r) => r.objective === 'conversion');
  return { current: own(rows.current), previous: own(rows.previous), cut: own(rows.cut), cutPrevious: own(rows.cutPrevious) };
};

/**
 * Box 3 and the GA4 tiles, in the registry order. Cut tiles read rows.cut /
 * rows.cutPrevious; the others read rows.current / rows.previous. The rows are
 * read as the caller (buildChannelView) filtered them, `objective` included;
 * under "all" CPA, CPL and ROAS keep only their Conversion rows (rules-04 E4).
 * Those three name the objective they read: Conversion under "all", else the
 * selected one, over the same rows as before (rules-04 H1). `objectivesInScope`
 * holds the objectives the channel has current rows of under the page's other
 * filters, in every currency (buildChannelView's): when the page has rows and
 * the named objective is not among them, those three read "—" and say why
 * (rules-04 H2). Without it nothing is known, and no tile says why.
 */
export const tilesOf = (
  platform: ChannelPlatform, rows: TileRows, objective: ObjectiveFilter, objectivesInScope?: ReadonlySet<string>,
): PlatformTile[] => {
  // Box 2's rule on box 2's rows (rules-04 D1): the period's impressions, a cut tile's too.
  const low = isLowSample(totalsOf(rows.current));
  const conversion = objective === 'all' ? conversionRowsOf(rows) : null;
  // A narrowed tile is the tile the Conversion filter shows, so its n<500 reads its own rows.
  const conversionLow = conversion !== null && isLowSample(totalsOf(conversion.current));
  const named = objective === 'all' ? 'conversion' : objective;
  // A Conversion row in another currency is in scope, so the plain "—" stays: "no Conversion campaigns" would be false.
  const absent = objectivesInScope !== undefined && rows.current.length > 0 && !objectivesInScope.has(named);
  return PLATFORM_TILES[platform].map((s): PlatformTile => {
    if (!CONVERSION_ONLY.has(s.metric)) return tileOf(platform, s, rows, low);
    const tile = conversion === null ? tileOf(platform, s, rows, low) : tileOf(platform, s, conversion, conversionLow);
    // The line explains a "—" only: it never sits under a figure.
    return { ...tile, objective: named, noObjectiveRows: absent && tile.state === 'none' };
  });
};

// ---------------------------------------------------------------- GA4 chart

/**
 * The GA4 chart (spec §3.5, R15) on box 4's grain, because its buckets ARE box
 * 4's: the grain, the keys and each bucket's ad spend are taken from
 * trendOf(adSpend, range) itself, not recomputed (one point per day up to
 * TREND_DAILY_MAX_DAYS, else per ISO week keyed by its Monday; every bucket of
 * `range`, in order). Only the GA rows are bucketed here, by that same key.
 * `ga` is site-wide and never filtered; `adSpend` (get_overview_facts, every ad
 * channel) arrives already narrowed to the page's objective and currency. A
 * bucket no row reports is null: a gap, never 0.
 */
export const ga4PointsOf = (
  ga: readonly ChannelFactRow[],
  adSpend: readonly FactRow[],
  range: DateRange,
): { grain: 'day' | 'week'; points: Ga4Point[] } => {
  const { grain, points: spendPoints } = trendOf(adSpend, range);
  const keyOf = (d: string): string => (grain === 'day' ? d : isoWeekStart(d));
  const points = spendPoints.map((p): Ga4Point => {
    const own = ga.filter((r) => keyOf(r.date) === p.key);
    return {
      key: p.key,
      sessions: sumOf(own, (r) => r.sessions),
      webConversions: sumOf(own, (r) => r.conversions),
      adSpend: p.spend,
    };
  });
  return { grain, points };
};
